import { useEffect } from "react";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { InputFieldDigitsOnly } from "components/forms/InputFieldDigitsOnly";
import { ErrorPositions } from "enums/errorPositions";
import { FeatureFlags } from "featureFlags";
import { Tooltip } from "components/common/tooltip";
import { Icon } from "semantic-ui-react";
import styles from "features/Accounts/PackAssignmentModal/packAssignmentSteps/LicensesStep/LicenseCountInput/LicenseCountInput.module.scss";

export interface LicenseCountInputProps {
  packId: number;
  licensesCount?: number;
  assingedLicensesCount?: number;
  maxLicenseCount: number;
  onLicenseCountChange: (selectedPackId: number, licensesCount?: number) => void;
  errorPosition?: ErrorPositions;
  unlimitedLicenses?: boolean;
  label?: string | React.ReactNode;
}

export const LicenseCountInput: React.FC<LicenseCountInputProps> = ({
  packId,
  licensesCount,
  assingedLicensesCount,
  onLicenseCountChange,
  maxLicenseCount,
  unlimitedLicenses,
  errorPosition = ErrorPositions.top,
  label,
}) => {
  const paidPacks = useFeatureFlag(FeatureFlags.PaidPacks);

  useEffect(() => {
    if (unlimitedLicenses) {
      onLicenseCountChange(packId, undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onValueChange = (value?: number) => {
    onLicenseCountChange(packId, value);
  };

  const validateLicenseCount = (value: number) => {
    if (value <= 0) {
      return `Please enter a number greater than <strong>0</strong>`;
    }
    if (value > maxLicenseCount) {
      return `Please enter a number smaller than <strong>${maxLicenseCount.toLocaleString("en-US")}</strong>`;
    } else if (assingedLicensesCount && assingedLicensesCount > value && value > 0) {
      return `Value must be greater than or equal to the current number of licenses in use. Current licenses: <strong>${assingedLicensesCount.toLocaleString()}</strong>`;
    } else {
      return "";
    }
  };

  return (
    <>
      {label}
      {paidPacks && unlimitedLicenses ? (
        <div className={styles.counter}>
          <b>Unlimited</b>&nbsp;(Auto-increment)&nbsp;
          <Tooltip
            hoverable
            target={<Icon className="info circle" />}
            position="top left"
            content={TOOLTIP_CONTENT}
            styles={{ width: "24.2rem" }}
            showAlways
          />
        </div>
      ) : (
        <InputFieldDigitsOnly
          value={licensesCount || ""}
          onChange={onValueChange}
          validate={validateLicenseCount}
          className={styles.counter}
          errorPosition={errorPosition}
          dataTestId="licenses-count-input"
          propertyName="licensesCount"
          placeholder="Quantity"
          fill
        />
      )}
    </>
  );
};

const TOOLTIP_CONTENT =
  "Will automatically adjust license quantities based on usage when this pack is enabled for a customer.  Recommended for free packs to avoid manual license increases.";
